(() => {
  if (window.EzupFormsAnalytics || window.Shopify?.designMode || !window.IntersectionObserver || !window.crypto?.randomUUID) return;
  const nativeFetch = window.fetch.bind(window);
  const aliases = new Map();
  const views = new Map();
  const elements = new Map();
  let configured = false;
  let configuring = false;
  let scheduled = false;
  let configurationAttempts = 0;
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;

  function identifier(endpoint) {
    try {
      const url = new URL(endpoint, location.origin);
      return url.origin === location.origin ? url.pathname.match(/\/apps\/forms\/([a-z0-9_-]+)\/?$/)?.[1] : null;
    } catch { return null; }
  }

  function visible(element) {
    if (document.visibilityState !== "visible" || !element.isConnected || element.closest('[hidden], [aria-hidden="true"]')) return false;
    if (element.checkVisibility && !element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return false;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= innerHeight || rect.right <= 0 || rect.left >= innerWidth) return false;
    for (let node = element; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
    }
    return true;
  }

  function send(formId, view) {
    if (view.sending || view.sent || view.attempts >= 3 || !allowed()) return;
    view.sending = true;
    view.attempts++;
    nativeFetch("/apps/forms/views", {
      method: "POST", credentials: "same-origin", keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ form: formId.replace(/^form_/, ""), viewId: view.id }),
    }).then(response => { view.sent = response.ok; }).catch(() => {}).finally(() => {
      view.sending = false;
      if (!view.sent && view.attempts < 3) setTimeout(() => send(formId, view), view.attempts * 2000);
    });
  }

  function observeView(element) {
    if (!allowed() || !visible(element)) return;
    const formId = elements.get(element);
    if (!formId) return;
    let view = views.get(formId);
    if (!view) {
      view = { id: crypto.randomUUID(), sent: false, sending: false, attempts: 0 };
      views.set(formId, view);
    }
    send(formId, view);
    observer.unobserve(element);
  }

  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) observeView(entry.target);
  }, { threshold: 0 });

  function register(element, endpoint) {
    const formId = aliases.get(identifier(endpoint));
    if (!element || !formId) return;
    if (elements.get(element) !== formId) {
      elements.set(element, formId);
      observer.observe(element);
    }
    if (!views.has(formId)) observeView(element);
  }

  function scan() {
    scheduled = false;
    if (!configured) { configure(); return; }
    if (!allowed()) return;
    for (const [element] of elements) if (!element.isConnected) { observer.unobserve(element); elements.delete(element); }
    document.querySelectorAll("form").forEach(form => {
      let endpoint = form.getAttribute("data-forms-endpoint") || form.getAttribute("data-package-post-url") || form.getAttribute("action");
      const newsletter = form.closest("newsletter-form");
      if (newsletter) endpoint = newsletter.endpoint || newsletter.getAttribute("endpoint") || "/apps/forms/newsletter";
      try {
        const settings = JSON.parse(form.getAttribute("data-form-settings") || "{}");
        endpoint = form.getAttribute("data-package-post-url") || settings.email_post_url || endpoint;
      } catch {}
      if (["upload-artwork-form", "industrial-upload-artwork-form"].includes(form.id)) endpoint = "/apps/forms/upload-design";
      register(form, endpoint);
    });
    document.querySelectorAll("contact-form-handler[form-id]").forEach(handler => {
      const form = handler.form || document.getElementById(handler.getAttribute("form-id"));
      register(form, handler.emailPostUrl || handler.getAttribute("email-post-url"));
    });
    document.querySelectorAll("upload-logo-dialog[data-endpoint]").forEach(dialog => register(dialog.querySelector(".dialog") || dialog, dialog.getAttribute("data-endpoint")));
    document.querySelectorAll("[data-custom-artwork-line]").forEach(row => register(row, "/apps/forms/custom-central"));
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scan);
  }

  function getViewId(endpoint) {
    if (!allowed()) return null;
    const formId = aliases.get(identifier(endpoint));
    return views.get(formId)?.id || null;
  }

  window.EzupFormsAnalytics = { getViewId };

  function configure() {
    if (!allowed()) return;
    if (configured) { schedule(); return; }
    if (configuring || configurationAttempts >= 3) return;
    configuring = true;
    configurationAttempts++;
    nativeFetch("/apps/forms/views", { credentials: "same-origin" }).then(response => {
      if (!response.ok) throw new Error("Tracking unavailable");
      return response.json();
    }).then(({ forms }) => {
      for (const form of forms) {
        aliases.set(form.id, form.id);
        aliases.set(form.id.replace(/^form_/, ""), form.id);
        aliases.set(form.slug, form.id);
      }
      configured = true;
      schedule();
    }).catch(() => { setTimeout(configure, configurationAttempts * 2000); }).finally(() => { configuring = false; });
  }

  document.addEventListener("visitorConsentCollected", configure);
  document.addEventListener("visibilitychange", configure);
  new MutationObserver(schedule).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ["class", "style", "hidden", "aria-hidden", "open", "action", "endpoint", "email-post-url"] });
  // Existing visitor permission can become available after the API loads,
  // without visitorConsentCollected (which is emitted for a new choice).
  // Recheck readiness briefly; every request still requires actual permission.
  let readinessChecks = 0;
  function checkReadiness() {
    configure();
    if (!configured && ++readinessChecks < 30) setTimeout(checkReadiness, 1000);
  }
  if (window.Shopify?.customerPrivacy) checkReadiness();
  else window.Shopify?.loadFeatures?.([{ name: "consent-tracking-api", version: "0.2" }], error => { if (!error) checkReadiness(); });
})();
